import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

interface JoinMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJoin: (name: string, userId: string) => void;
  meetingCode: string;
}

export const JoinMeetingDialog: React.FC<JoinMeetingDialogProps> = ({
  open,
  onOpenChange,
  onJoin,
  meetingCode,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [name, setName] = useState<string>(user?.fullName || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!name.trim()) {
        return;
      }

      setIsSubmitting(true);
      
      try {
        let userId: string;
        
        if (isAuthenticated && user?.id) {
          // Nếu đã đăng nhập, sử dụng ID của user
          userId = user.id;
        } else {
          // Nếu chưa đăng nhập, tự động generate ID
          userId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        }

        await onJoin(name.trim(), userId);
        onOpenChange(false);
      } catch (error) {
        console.error("Error joining meeting:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, isAuthenticated, user, onJoin, onOpenChange]
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Tham gia cuộc họp</DialogTitle>
          <DialogDescription>
            {isAuthenticated
              ? "Vui lòng xác nhận tên của bạn để tham gia cuộc họp"
              : "Vui lòng nhập tên của bạn để tham gia cuộc họp. Bạn cũng có thể đăng nhập để sử dụng tài khoản của mình."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Tên của bạn</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nhập tên của bạn"
                required
                disabled={isSubmitting}
                autoFocus
                aria-label="Tên của bạn"
              />
            </div>
            {!isAuthenticated && (
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">
                  Bạn đang tham gia với tư cách khách. Để sử dụng đầy đủ tính năng, vui lòng{" "}
                  <Link
                    to={`/login?callbackUrl=/meeting/${meetingCode}`}
                    className="text-primary underline hover:no-underline"
                    onClick={handleCancel}
                  >
                    đăng nhập
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
              aria-label="Hủy"
            >
              Hủy
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              aria-label="Tham gia cuộc họp"
            >
              {isSubmitting ? "Đang kết nối..." : "Tham gia"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

